import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps } from 'src/Library/Types';
import Button from 'antd/lib/button';
import Modal from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Input from 'antd/lib/input';
import DatePicker from 'antd/lib/date-picker';
import Checkbox from 'antd/lib/checkbox';
import message from 'antd/lib/message';
import Upload from 'antd/lib/upload';
import Icon from 'antd/lib/icon';
import { FFConnectionRequestKYCStyles } from './FFConnectionRequestKYC.styles';
import { enterKYCText } from './KYCConnections.common';
import Form, { FormComponentProps } from 'antd/lib/form';
import { KYCData, KYCInfo } from './KycConnections.types';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { arrayToObject, getFileExtension } from 'src/utils/utils';
import { isValidArray, isValidString } from 'src/Library/Helper';
import { useGenericState } from 'src/Library/UseGenericState';
const dataSecurity = require('src/assets/images/dataSecurity.png');
import moment from 'moment';
import { sendConnectionRequest } from 'src/api/masters';
import { getS3Links, uploadToS3 } from 'src/api/invoice';

interface AddKYCFormProps extends
  StylesProps<ReturnType<typeof FFConnectionRequestKYCStyles>>, FormComponentProps {
  isVisible: boolean;
  kycData: KYCData [];
  onCancel: (success?:boolean) => void;
  targetOrganisationId?: string;
  createBooking: () => void;
  isGeneratingBookingData: boolean;
}

interface StateProps {
  isDisclaimerChecked: boolean;
  isLoading?: boolean;
  isShowMore: boolean;
  isUploading: boolean;
  fileUrl: string;
  fileName: string;
}

const AddKYCForm = (props: AddKYCFormProps): React.ReactElement => {

  const {
    isVisible,
    classes,
    form,
    kycData,
    onCancel,
    targetOrganisationId,
    isGeneratingBookingData,
  } = props;

  const kycDataMap = isValidArray(kycData) ? arrayToObject(kycData, 'code') : {};

  const [state, setState] = useGenericState<StateProps>({
    isDisclaimerChecked: kycDataMap.DISCLAIMER?.defaultValue,
    isShowMore: false,
    isUploading: false,
    fileUrl: kycDataMap.ADDRESS_PROOF?.defaultValue?.fileUrl,
    fileName: kycDataMap.ADDRESS_PROOF?.defaultValue?.fileName,
  });

  const {
    isDisclaimerChecked,
    isLoading,
    isUploading,
    fileUrl,
    fileName,
  } = state;

  const renderHeader = () => {
    return (
      <Row>
        <Col span={16}>
          <div className={classes.enterKYCText}>Enter KYC Details</div>
          <div className={classes.kycText}>{enterKYCText}</div>
        </Col>
        <Col span={8}>
          <div className={classes.secureData}>
            <img src={dataSecurity} className={classes.secureDataImg} />
            Don't worry! <br/> Your data is safe with us.</div>
        </Col>
      </Row>
    );
  };

  const renderDateAndUserName = () => {
    const { DATE : date, USER_NAME: userName } = kycDataMap;
    const dateFormat = date?.metadata?.dateFormat?.toUpperCase();
    return (
      <Row>
        <Col span={16}>
          Date:
          <Form.Item className={classes.formInput} style={{ marginTop: '8px' }}>
            {form.getFieldDecorator(`${date?.code}`, {
              rules: [{ required: date?.isMandatory,
                message: `${date?.name} is required` }],
              initialValue: date?.defaultValue ?
                moment() : undefined,
            })(
              <DatePicker
                placeholder="Date"
                disabledDate={(current) => {
                  return !!current && current < moment().startOf('day');
                }}
                className={classNames(classes.input, classes.datePicker)}
                format={date?.metadata?.dateFormat?.toUpperCase()}
              ></DatePicker>
            )}
          </Form.Item>
        </Col>
        <Col span={8}>
          User Name:
          <Form.Item className={classes.formInput} style={{ marginTop: '8px' }}>
            {form.getFieldDecorator(`${userName?.code}`, {
              rules: [{ required: userName?.isMandatory,
                message: `${userName?.name} is required` },
                { min: userName?.metadata?.minLength,
                  message: `${userName?.name} must have atleast
                  ${userName?.metadata?.minLength} character(s)` },
              ],
              initialValue: userName?.defaultValue,
            })(
              <Input
                placeholder={userName?.metadata?.placeholder}
                className={classes.input}
              ></Input>
            )}
          </Form.Item>
        </Col>
      </Row>
    );
  };

  const handleSubmit = () => {
    if (!fileUrl || !fileName) {
      message.error('Please upload address proof');
    } else {
      const kycCodeList = ['ADDRESS_PROOF', 'DATE'];
      form.validateFieldsAndScroll(async (err, values) => {
        if (!isDisclaimerChecked) {
          message.error('Please accept the disclaimer to proceed.');
          return;
        }
        if (err) return;
        let kycData : KYCInfo [] = [];
        Object.keys(values)?.map(key => {
          kycCodeList.indexOf(key) === -1 && kycData.push({
            code: key,
            value: values[key],
          });
        });
        kycData = [
          ...kycData,
          {
            code: 'DATE',
            value: moment(values.DATE).format('DD/MM/YYYY'),
          },
          {
            code: 'DISCLAIMER',
            value: isDisclaimerChecked,
          },
          {
            code: 'ADDRESS_PROOF',
            value: {
              fileName,
              fileUrl,
            },
          },
        ];
        if (targetOrganisationId) {
          setState({
            isLoading: true,
          });
          const response = await sendConnectionRequest({
            targetOrganisationId,
            kycData,
          });
          if (response.isSuccess) {
            message.success('Connection Request sent successfully')
            onCancel(true);
          } else {
            message.error(response.errorMessage);
          }
          setState({
            isLoading: false,
          });
        }
      });
    }
  };

  const s3LinkRequest = async ({ file }): Promise<void> => {
    setState({
      isUploading: true,
      fileName: file.name,
    });
    const extension = getFileExtension(file.name);
    const response = await getS3Links({
      extension,
      fileName: file.name,
      document_type: file.type,
      initialPath: 'KYC',
    });
    let fileUrl: string = '';
    if (response.isSuccess) {
      const upload = await uploadToS3(response.data.post, extension, file);
      if (upload.isSuccess) fileUrl = response.data.get;
    } else {
      message.error(response.errorMessage);
    }
    setState({
      fileUrl,
      isUploading: false,
    });
  };

  const renderFormField = (item) => {
    switch (item.format) {
      case 'TEXTAREA': return (
        <Input.TextArea
          placeholder={item.metadata?.placeholder}
          className={classes.input}
          rows={3}
        />
      );
      case 'FILE_UPLOAD': return (
        <React.Fragment>
          <Upload
            showUploadList={false}
            multiple={false}
            customRequest={file => s3LinkRequest(file)}
            disabled={isUploading}>
            <Button loading={isUploading} ><Icon type="upload" /> Upload</Button>
          </Upload>
          <div style={{ fontSize: '12px' }}>
            {fileName}
            {fileName && <Icon type="close" style={{ color: 'red', marginLeft: '8px' }}
              onClick={() => setState({ fileName: '', fileUrl: '' })} />}
          </div>
        </React.Fragment>
      );
      default: return (
        <Input
          placeholder={item.metadata?.placeholder}
          className={classes.input}
        ></Input>
      );
    }
  }

  const renderForm = () => {
    const fieldsToShow = ['TEXT', 'FILE_UPLOAD', 'TEXTAREA'];
    return (
      <div style={{ margin: '36px 0' }}>
        {kycData?.map(item => (
          fieldsToShow.indexOf(item.format) !== -1 && item.code !== 'USER_NAME' && <Row>
            <Col span={5} style={{ marginTop: '10px' }}>
              {item.name}:
            </Col>
            <Col span={19}>
             <Form.Item className={classes.formInput}>
                {form.getFieldDecorator(`${item.code}`, {
                  rules: item.format !== 'FILE_UPLOAD' ? [
                    { required: item.isMandatory, message: `${item.name} is required` },
                      { pattern: item.metadata?.regex,
                        message: 'Invalid format' },
                      { min: item.metadata?.minLength,
                        message: `${item.name} must have atleast ${item.metadata?.minLength} character(s)` },
                      { max: item.metadata?.maxLength,
                        message: `Maximum ${item.metadata?.maxLength} characters allowed` },
                    ] : undefined,
                  initialValue: item.format !== 'FILE_UPLOAD' ? item.defaultValue : undefined,
                })(
                  renderFormField(item)
                )}
              </Form.Item>
            </Col>
          </Row>
        ))}
      </div>
    );
  };

  const renderDisclaimer = () => {
    return (
      <div style={{ marginBottom: '36px' }}>
        <div style={{ display: 'flex' }}>
          <Checkbox
            key={kycDataMap.DISCLAIMER?.code}
            style={{ marginRight: '8px' }}
            checked={isDisclaimerChecked}
            onChange={e => setState({ isDisclaimerChecked: e.target.checked })}
          />
          {isValidString(kycDataMap.DISCLAIMER?.name) &&
          <div className={classes.disclaimer}
            // style={{ display: isShowMore ? 'block' : '-webkit-box' }}
          >
              {kycDataMap.DISCLAIMER?.name}</div>}
        </div>
        {/* {<span onClick={() => setState({ isShowMore: !isShowMore })}
          className={classes.showAll}>
          {!isShowMore ? 'Show More' : 'Show Less'}</span>} */}
      </div>
    );
  };

  return (
    <Modal
      visible={isVisible}
      zIndex={1000}
      className={classes.kycModal}
      title="Request For Connection"
      closable={false}
      destroyOnClose
      footer={
        <React.Fragment>
          <span className={classes.skipButton}
            onClick={() => onCancel()}>Cancel</span>
          <Button
            className={classes.okButton}
            onClick={() => handleSubmit()}
            loading={isLoading || isGeneratingBookingData}
            disabled={isUploading}
          >Submit</Button>
        </React.Fragment>
      }
    >
      {renderHeader()}
      {renderForm()}
      {renderDisclaimer()}
      {renderDateAndUserName()}
    </Modal>
  );
};

const mapStateToProps = ({ discoverRatesReducer }, ownProps) => ({
  selectedFilters: discoverRatesReducer.selectedFilters,
});

export default connect(mapStateToProps,
  null)(Form.create()(withStyles(FFConnectionRequestKYCStyles)(AddKYCForm)));
